import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getMailcomSupply,
  type MailcomDrainingParent,
  type MailcomDrainingReason,
  type MailcomSupplyResponse,
} from '../../api/endpoints.js';
import { useApiQuery } from '../../api/hooks.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  StatCard,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../../ui/index.js';
import { TextTokenBreaks } from '../user-inspect/SoftBreaks.js';
import { RENEWAL_STATUS_LABEL, fmtDate, toneForRenewal } from './shared.js';
import {
  DRAINING_ACTIVITY_LABEL,
  DRAINING_REASON_HINT,
  DRAINING_REASON_LABEL,
  describeActivityHint,
  formatCount,
  formatPace,
  formatSinceDays,
  formatSupplyEta,
  isMailcomSupply,
} from './supply-format.js';

/**
 * WS5b (Q64 / Q66 / Q67) - mail.com supply visibility. Read-only: the numbers
 * come from GET /admin/providers/mailcom/summary/supply (one cached read
 * transaction on the server). Nothing here changes provider state.
 */
type SupplyQuery = ReturnType<typeof useApiQuery<MailcomSupplyResponse>>;

/**
 * The server caches the supply read for 60 s, so polling faster only returns
 * the same snapshot. Overlap-safe and tab-hidden-aware, like the summary poll.
 */
const SUPPLY_POLL_MS = 60_000;

function useSupplyQuery(): SupplyQuery {
  const query = useApiQuery((o) => getMailcomSupply(o), []);
  const reloadRef = useRef(query.reload);
  const busyRef = useRef(query.refreshing);
  reloadRef.current = query.reload;
  busyRef.current = query.refreshing;
  useEffect(() => {
    const tick = (): void => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (busyRef.current) return;
      reloadRef.current();
    };
    const id = window.setInterval(tick, SUPPLY_POLL_MS);
    return () => window.clearInterval(id);
  }, []);
  return query;
}

/** Parent-tab mount: owns its query so non-mail.com pages never fetch it. */
export function SupplyCardsConnected({ onOpenDraining }: { readonly onOpenDraining: () => void }): JSX.Element {
  const query = useSupplyQuery();
  return <SupplyCards query={query} onRefresh={query.reload} onOpenDraining={onOpenDraining} />;
}

/** Draining-tab mount. */
export function DrainingPanelConnected(): JSX.Element {
  const query = useSupplyQuery();
  return <DrainingPanel query={query} onRefresh={query.reload} />;
}

const DRAINING_PAGE_SIZE = 25;

const TIPS = {
  headroom:
    'Aktif parent’ların toplam kalan ömür boyu alias üretim hakkı: her parent için max(0, 99 − şimdiye kadar üretilen alias). Silinmiş eski alias satırları da üretilmiş sayılır. Gizli (V2 öncesi) üretimler yalnızca düşürür: bu sayı ÜST SINIRDIR.',
  eta:
    'Kalan toplam hak ÷ son 6 saatteki tüketim hızı (alias/saat). Tüketim = son 6 saatte satılan (leased → used) alias. Hız saatten saate değişir; yalnızca yaklaşık bir ufuk verir.',
  atCap:
    'Aktif parent’lardan en az 91 alias üretilmiş ya da limit kuralıyla emekli edilmiş olanlar: yeni alias üretemezler.',
  nearCap: 'Aktif parent’lardan 80–90 alias üretilmiş olanlar: limite çok yakın.',
  capWaiting:
    'Limit yeniden deneme beklemesindeki parent’lar (askıda, alias_cap_wait): üretim 0 çıktı, sıradaki deneme zamanı gelene kadar bekliyorlar. Draining değiller, bu yüzden Draining sekmesinde görünmezler.',
  fullCycle: 'Hâlâ tam 9 alias’lık bir döngüye yetecek hakkı (kalan ≥ 9) olan aktif parent sayısı.',
  stock: 'Aktif parent’larda şu an satışa hazır (available) alias; üretim hakkından ayrı, hazır stok.',
} as const;

/** Supply cards (headroom, remaining time, at / near cap) for the Parent tab. */
export function SupplyCards({
  query,
  onRefresh,
  onOpenDraining,
}: {
  readonly query: SupplyQuery;
  readonly onRefresh: () => void;
  readonly onOpenDraining: () => void;
}): JSX.Element {
  const raw = query.kind === 'ok' ? query.data ?? null : null;
  const data = isMailcomSupply(raw) ? raw : null;
  const malformed = query.kind === 'ok' && data === null;
  return (
    <Card flush data-testid="mailcom-supply-cards">
      <CardHeader
        title="Alias Arzı (ömür boyu limit)"
        actions={
          <Button variant="ghost" size="sm" onClick={onOpenDraining} data-testid="supply-open-draining">
            Draining parent’lar
          </Button>
        }
      />
      <CardBody>
        {query.kind === 'loading' || query.kind === 'idle' ? <LoadingState label="Arz durumu yükleniyor…" /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onRefresh} /> : null}
        {malformed ? <ErrorState reason="malformed_response" onRetry={onRefresh} /> : null}
        {data?.stale ? (
          <Banner tone="warn" testId="supply-stale">
            Arz verisi şu an okunamadı; son başarılı okuma gösteriliyor ({fmtDate(data.generatedAt)}).
          </Banner>
        ) : null}
        {data ? <SupplyCardsBody data={data} /> : null}
      </CardBody>
    </Card>
  );
}

function SupplyCardsBody({ data }: { readonly data: MailcomSupplyResponse }): JSX.Element {
  const { active, all, pace, estimate, cap } = data;
  const etaText = formatSupplyEta(estimate.hoursRemaining);
  return (
    <>
      <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
        mail.com her parent için ömür boyu en fazla {cap.lifetimeCap} alias üretmeye izin verir (silinenler dahil).
        Aşağıdaki sayılar yalnızca <strong>aktif</strong> parent’ları kapsar; ömür limiti dolan parent’lar satışa
        geri dönmez.
      </p>
      <div className="kpi-grid" data-testid="supply-kpi-grid">
        <StatCard
          label="Kalan toplam alias hakkı"
          value={formatCount(active.headroom)}
          meta={<span title={TIPS.headroom} className="mcp-stat-hint">{TIPS.headroom}</span>}
          testId="supply-card-headroom"
        />
        <StatCard
          label="Tahmini kalan süre"
          value={etaText}
          meta={
            <span title={TIPS.eta} className="mcp-stat-hint">
              {formatPace(pace.consumed, pace.perHour, pace.windowHours)}.
              {estimate.withStockHoursRemaining !== null
                ? ` Hazır stok dahil: ${formatSupplyEta(estimate.withStockHoursRemaining)}.`
                : ''}
            </span>
          }
          testId="supply-card-eta"
        />
        <StatCard
          label={`Limitte (≥${cap.reachedAt})`}
          value={formatCount(active.atCap)}
          meta={
            <span title={TIPS.atCap} className="mcp-stat-hint">
              Aktif parent. Tüm durumlarda limitte: {formatCount(all.atCap)} / {formatCount(all.parents)}.
            </span>
          }
          testId="supply-card-at-cap"
        />
        <StatCard
          label={`Limite yakın (${cap.nearFrom}–${cap.nearTo})`}
          value={formatCount(active.nearCap)}
          meta={
            <span title={TIPS.nearCap} className="mcp-stat-hint">
              Aktif parent. Tüm durumlarda: {formatCount(all.nearCap)}.
              {typeof all.capWaiting === 'number' && all.capWaiting > 0
                ? <span title={TIPS.capWaiting} data-testid="supply-cap-waiting"> Limit bekleyen (askıda): {formatCount(all.capWaiting)}.</span>
                : null}
            </span>
          }
          testId="supply-card-near-cap"
        />
        <StatCard
          label="Tam döngü hakkı olan parent"
          value={formatCount(active.withFullCycleHeadroom)}
          meta={
            <span title={TIPS.fullCycle} className="mcp-stat-hint">
              {formatCount(active.parents)} aktif parent içinde.
            </span>
          }
          testId="supply-card-full-cycle"
        />
        <StatCard
          label="Hazır alias stoku"
          value={formatCount(active.liveStock)}
          meta={<span title={TIPS.stock} className="mcp-stat-hint">{TIPS.stock}</span>}
          testId="supply-card-stock"
        />
      </div>
    </>
  );
}

type ReasonFilter = 'all' | MailcomDrainingReason;
const REASON_ORDER: readonly MailcomDrainingReason[] = ['stuck_below_cap', 'near_cap', 'cap_reached'];

function toneForReason(r: MailcomDrainingReason): 'danger' | 'warn' | 'info' {
  if (r === 'stuck_below_cap') return 'danger';
  if (r === 'near_cap') return 'warn';
  return 'info';
}

/** "Draining" panel: every draining parent with its reason and since-when (Q67). */
export function DrainingPanel({
  query,
  onRefresh,
}: {
  readonly query: SupplyQuery;
  readonly onRefresh: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState<ReasonFilter>('all');
  const [shown, setShown] = useState(DRAINING_PAGE_SIZE);
  const raw = query.kind === 'ok' ? query.data ?? null : null;
  const data = isMailcomSupply(raw) ? raw : null;
  const malformed = query.kind === 'ok' && data === null;

  const rows = useMemo<readonly MailcomDrainingParent[]>(() => {
    if (!data) return [];
    return filter === 'all' ? data.draining.rows : data.draining.rows.filter((r) => r.reason === filter);
  }, [data, filter]);

  const select = (f: ReasonFilter): void => {
    setFilter(f);
    setShown(DRAINING_PAGE_SIZE);
  };

  return (
    <Card flush data-testid="mailcom-draining-panel">
      <CardHeader
        title="Draining parent’lar"
        actions={
          <Button variant="ghost" size="sm" onClick={onRefresh} icon={<Icon.Refresh size={12} />}>
            Yenile
          </Button>
        }
      />
      <CardBody>
        <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
          Draining, yeni alias satmayan ama yenileme/temizlik bekleyen parent demektir. Her satır parent’ın neden
          draining’de olduğunu ve ne zamandan beri orada kaldığını gösterir. “Takılı” = yenileme deniyor ama
          sonuç yok; “donmuş” = son {data?.draining.frozenAfterHours ?? 24} saatte hiç yenileme işi çalışmadı.
        </p>

        {query.kind === 'loading' || query.kind === 'idle' ? <LoadingState label="Draining listesi yükleniyor…" /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onRefresh} /> : null}
        {malformed ? <ErrorState reason="malformed_response" onRetry={onRefresh} /> : null}
        {data?.stale ? (
          <Banner tone="warn">Son başarılı okuma gösteriliyor ({fmtDate(data.generatedAt)}).</Banner>
        ) : null}
        {data?.draining.truncated ? (
          <Banner tone="warn" testId="draining-truncated">
            Liste kısaltıldı: {formatCount(data.draining.total)} draining parent’tan{' '}
            {formatCount(data.draining.rows.length)} tanesi gösteriliyor.
          </Banner>
        ) : null}

        {data ? (
          <>
            <div
              role="group"
              aria-label="Neden filtresi"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 12px' }}
              data-testid="draining-reason-filter"
            >
              <Button
                variant={filter === 'all' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => select('all')}
                aria-pressed={filter === 'all'}
                data-testid="draining-filter-all"
              >
                Tümü ({formatCount(data.draining.rows.length)})
              </Button>
              {REASON_ORDER.map((r) => (
                <Button
                  key={r}
                  variant={filter === r ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => select(r)}
                  aria-pressed={filter === r}
                  title={DRAINING_REASON_HINT[r]}
                  data-testid={`draining-filter-${r}`}
                >
                  {DRAINING_REASON_LABEL[r]} ({formatCount(data.draining.byReason[r])})
                </Button>
              ))}
            </div>

            {filter !== 'all' ? (
              <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>{DRAINING_REASON_HINT[filter]}</p>
            ) : null}

            {rows.length === 0 ? (
              <EmptyState label="Bu filtrede draining parent yok." />
            ) : (
              <>
                <Table dense stack scroll className="mcp-supply-table tbl-stack-xl" data-testid="draining-table">
                  <THead>
                    <TRow>
                      <TH>Parent</TH>
                      <TH>Neden</TH>
                      <TH>Durum</TH>
                      <TH style={{ textAlign: 'right' }}>Üretilen / kalan</TH>
                      <TH>Ne zamandan beri</TH>
                      <TH>Son yenileme işi</TH>
                      <TH>Limit yeniden deneme</TH>
                    </TRow>
                  </THead>
                  <TBody>
                    {rows.slice(0, shown).map((r) => (
                      <DrainingRow key={r.id} row={r} frozenAfterHours={data.draining.frozenAfterHours} />
                    ))}
                  </TBody>
                </Table>
                {rows.length > shown ? (
                  <div style={{ marginTop: 12, textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShown((n) => n + DRAINING_PAGE_SIZE)}
                      data-testid="draining-show-more"
                    >
                      Daha fazla göster ({formatCount(rows.length - shown)} kaldı)
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}

function DrainingRow({
  row,
  frozenAfterHours,
}: {
  readonly row: MailcomDrainingParent;
  readonly frozenAfterHours: number;
}): JSX.Element {
  const cap = row.capRetry;
  const hasCapState =
    cap.streak !== null || cap.lastZeroMintAt !== null || cap.nextRetryAt !== null || cap.retiredAt !== null;
  const job = row.lastJob;
  return (
    <TRow data-testid={`draining-row-${row.accountHandle}`}>
      <TD className="t-mono t-xs tbl-stack-primary" title={`ID: ${row.id}`}>
        <TextTokenBreaks text={row.accountHandle} />
      </TD>
      <TD className="tbl-stack-status" label="Neden">
        <Badge tone={toneForReason(row.reason)} dot title={DRAINING_REASON_HINT[row.reason]}>
          {DRAINING_REASON_LABEL[row.reason]}
        </Badge>
      </TD>
      <TD label="Durum" title={describeActivityHint(row.activity, frozenAfterHours)}>
        <span className="t-small" data-testid={`draining-activity-${row.accountHandle}`}>
          {DRAINING_ACTIVITY_LABEL[row.activity]}
        </span>
      </TD>
      <TD className="t-mono t-num" label="Üretilen / kalan" style={{ textAlign: 'right' }}>
        <span
          style={{
            color: row.remaining === 0 ? 'var(--danger-600)' : undefined,
            fontWeight: row.remaining === 0 ? 600 : undefined,
          }}
        >
          {row.created} / {row.remaining}
        </span>
      </TD>
      <TD className="t-small" label="Ne zamandan beri" title={fmtDate(row.since)}>
        {formatSinceDays(row.sinceDays)}
        <span className="t-tertiary t-xs">
          {' '}· {row.since ? new Date(row.since).toLocaleDateString('tr-TR') : '—'}
          {row.sinceSource === 'updated_at' ? ' (son güncelleme)' : ''}
        </span>
      </TD>
      <TD className={job?.error ? 'tbl-stack-full' : undefined} label="Son yenileme işi">
        {job ? (
          <span className="t-small">
            <Badge tone={toneForRenewal(job.status)} dot>{RENEWAL_STATUS_LABEL[job.status] ?? job.status}</Badge>{' '}
            <span className="t-tertiary t-xs">{fmtDate(job.at)}</span>
            {job.error ? (
              <span className="t-mono t-xs" style={{ display: 'block', marginTop: 2 }} title={job.error}>
                <TextTokenBreaks text={job.error} />
              </span>
            ) : null}
          </span>
        ) : (
          <span className="t-tertiary t-xs">iş yok</span>
        )}
      </TD>
      <TD className="t-xs" label="Limit yeniden deneme">
        {hasCapState ? (
          <span>
            {cap.streak !== null ? <span>boş tur serisi: {cap.streak}</span> : null}
            {cap.nextRetryAt ? <span style={{ display: 'block' }}>sonraki: {fmtDate(cap.nextRetryAt)}</span> : null}
            {cap.lastZeroMintAt ? (
              <span className="t-tertiary" style={{ display: 'block' }}>son boş tur: {fmtDate(cap.lastZeroMintAt)}</span>
            ) : null}
            {cap.retiredAt ? <span style={{ display: 'block' }}>emekli: {fmtDate(cap.retiredAt)}</span> : null}
          </span>
        ) : (
          <span className="t-tertiary">—</span>
        )}
      </TD>
    </TRow>
  );
}
